/**
 * Template Selector Component
 *
 * Modal overlay showing all available dashboard templates as cards.
 * User can preview template details and create a new dashboard from a template.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { useState, useCallback } from 'react';
import {
  X,
  Tv,
  LayoutDashboard,
  Truck,
  Package,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Presentation,
  Headphones,
  Sparkles,
  Crown,
  Monitor,
  Users,
  ChevronRight,
} from 'lucide-react';
import { useDashboard } from '../DashboardContext';
import { DASHBOARD_TEMPLATES, DashboardTemplate } from '../templates/dashboardTemplates';

interface TemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Map of template icons
 */
const TEMPLATE_ICONS: Record<string, React.FC<{ className?: string }>> = {
  Tv,
  LayoutDashboard,
  Truck,
  Package,
  Presentation: Monitor, // Fallback since Presentation may not exist
  Headphones,
  Sparkles,
  Crown,
};

/**
 * Color mapping for template cards
 */
const COLOR_MAP: Record<string, {
  bg: string;
  border: string;
  text: string;
  hoverBorder: string;
  badge: string;
}> = {
  blue: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    text: 'text-blue-400',
    hoverBorder: 'hover:border-blue-400',
    badge: 'bg-blue-500/20 text-blue-400',
  },
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    hoverBorder: 'hover:border-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-400',
  },
  violet: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    text: 'text-violet-400',
    hoverBorder: 'hover:border-violet-400',
    badge: 'bg-violet-500/20 text-violet-400',
  },
  cyan: {
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    hoverBorder: 'hover:border-cyan-400',
    badge: 'bg-cyan-500/20 text-cyan-400',
  },
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    hoverBorder: 'hover:border-amber-400',
    badge: 'bg-amber-500/20 text-amber-400',
  },
  pink: {
    bg: 'bg-pink-500/10',
    border: 'border-pink-500/30',
    text: 'text-pink-400',
    hoverBorder: 'hover:border-pink-400',
    badge: 'bg-pink-500/20 text-pink-400',
  },
  rose: {
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    text: 'text-rose-400',
    hoverBorder: 'hover:border-rose-400',
    badge: 'bg-rose-500/20 text-rose-400',
  },
  yellow: {
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/30',
    text: 'text-yellow-400',
    hoverBorder: 'hover:border-yellow-400',
    badge: 'bg-yellow-500/20 text-yellow-400',
  },
};

const defaultColor = COLOR_MAP.blue;

const TemplateCard: React.FC<{
  template: DashboardTemplate;
  onSelect: (template: DashboardTemplate) => void;
}> = ({ template, onSelect }) => {
  const colors = COLOR_MAP[template.color] ?? defaultColor;
  const IconComponent = TEMPLATE_ICONS[template.icon] ?? LayoutDashboard;

  return (
    <button
      onClick={() => onSelect(template)}
      className={`
        w-full text-left p-4 rounded-xl border-2 transition-all duration-200
        ${colors.bg} ${colors.border} ${colors.hoverBorder}
        hover:scale-[1.02] hover:shadow-lg hover:shadow-black/20
        active:scale-[0.98]
        group
      `}
    >
      {/* Icon + Title row */}
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg bg-gray-800/80 ${colors.text}`}>
          <IconComponent className="w-5 h-5" />
        </div>
        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors mt-1" />
      </div>

      {/* Name */}
      <h3 className="text-sm font-semibold text-white mb-1">{template.name}</h3>

      {/* Description */}
      <p className="text-xs text-gray-400 mb-3 line-clamp-2">{template.description}</p>

      {/* Metadata badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${colors.badge}`}>
          {template.audience}
        </span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-700/60 text-gray-400">
          {template.screen}
        </span>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-700/60 text-gray-400">
          <Users className="w-2.5 h-2.5 inline mr-0.5" />
          {template.widgets.length} widgets
        </span>
      </div>
    </button>
  );
};

const TemplateSelector: React.FC<TemplateSelectorProps> = ({ isOpen, onClose }) => {
  const { createFromTemplate } = useDashboard();
  const [loading, setLoading] = useState<string | null>(null);

  const handleSelectTemplate = useCallback(
    (template: DashboardTemplate) => {
      setLoading(template.id);

      // Small delay for visual feedback
      setTimeout(() => {
        createFromTemplate(template.id);
        setLoading(null);
        onClose();
      }, 300);
    },
    [createFromTemplate, onClose]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700/50">
          <div>
            <h2 className="text-lg font-bold text-white">Opprett fra mal</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Velg en forhåndskonfigurert dashboard-mal for din målgruppe
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Grid of templates */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {DASHBOARD_TEMPLATES.map((template) => (
              <div key={template.id} className="relative">
                {loading === template.id && (
                  <div className="absolute inset-0 z-10 bg-gray-900/80 rounded-xl flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <TemplateCard
                  template={template}
                  onSelect={handleSelectTemplate}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-6 py-3 border-t border-gray-700/50 text-center">
          <p className="text-[10px] text-gray-500">
            Du kan tilpasse alle widgets etter opprettelse via redigeringsmodus
          </p>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector;
